/**
 * Token buffering utility for streaming chat performance optimization.
 * Collects streaming tokens and flushes them in batches to reduce React re-renders.
 *
 * This utility is critical for performance - instead of triggering a React state update
 * for every single token received (which can be 1000+ times per second), we collect
 * tokens in memory and flush them periodically to achieve smooth 20fps updates.
 */

import type { FileCitation } from '../../types/chat.types';

/**
 * Interface for token buffer that accumulates streaming content and citations
 * before flushing them in batches to optimize React rendering performance.
 */
export interface TokenBuffer {
  /**
   * Adds a single content token to the buffer.
   * These tokens will be joined together when flushed.
   */
  addToken: (token: string) => void;

  /**
   * Adds a file citation to the buffer.
   * Citations are collected separately from content tokens.
   */
  addCitation: (citation: FileCitation) => void;

  /**
   * Flushes all buffered content and citations.
   * Returns the combined content string and citation array.
   * Clears the buffer after flushing.
   */
  flush: () => { content: string; citations: FileCitation[] };

  /**
   * Clears all buffered content and citations without returning them.
   * Useful for cleanup when streaming is aborted.
   */
  clear: () => void;

  /**
   * Checks if the buffer contains any content or citations.
   * Used to avoid unnecessary flush operations.
   */
  isEmpty: () => boolean;
}

/**
 * Creates a new token buffer instance for accumulating streaming content.
 *
 * Performance rationale: By buffering tokens in memory arrays instead of
 * immediately updating React state, we can reduce re-renders from ~1000/second
 * to ~20/second during streaming while maintaining perceived real-time response.
 *
 * @returns A new TokenBuffer instance with isolated state
 */
export function createTokenBuffer(): TokenBuffer {
  // Use arrays to efficiently collect tokens and citations
  // These will be joined/spread when flushed to minimize string concatenations
  let contentTokensBuffer: string[] = [];
  let citationsBuffer: FileCitation[] = [];

  return {
    addToken: (token: string) => {
      // Simply push to array - very fast operation
      // String concatenation is deferred until flush time
      contentTokensBuffer.push(token);
    },

    addCitation: (citation: FileCitation) => {
      // Store citations separately to maintain structure
      // Duplicate detection could be added here if needed
      citationsBuffer.push(citation);
    },

    flush: () => {
      // Join all content tokens into a single string
      // This is more efficient than concatenating strings repeatedly
      const combinedContent = contentTokensBuffer.join('');

      // Create a copy of citations to avoid reference issues
      const currentCitations = [...citationsBuffer];

      // Clear buffers immediately after capturing their content
      // This prevents memory leaks and prepares for next batch
      contentTokensBuffer = [];
      citationsBuffer = [];

      return {
        content: combinedContent,
        citations: currentCitations,
      };
    },

    clear: () => {
      // Reset both buffers to empty state
      // Used when streaming is aborted or component unmounts
      contentTokensBuffer = [];
      citationsBuffer = [];
    },

    isEmpty: () => {
      // Check if either buffer has content
      // Used to avoid unnecessary React state updates when nothing changed
      return contentTokensBuffer.length === 0 && citationsBuffer.length === 0;
    },
  };
}
